// client/src/contexts/PermissionsContext.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import type { PermissionKey } from '../types/permissions';

type PermissionsState = {
  ready: boolean;
  isAdmin: boolean;
  isAdminTeam: boolean;
  isAdminLeadership: boolean;
  permissions: Set<PermissionKey>;
  hasPermission: (key: PermissionKey) => boolean;
};

const PermissionsCtx = createContext<PermissionsState>({
  ready: false,
  isAdmin: false,
  isAdminLeadership: false, isAdminTeam: false,
  permissions: new Set(),
  hasPermission: () => false,
});

export function usePermissions(): PermissionsState {
  return useContext(PermissionsCtx);
}

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const [ready, setReady]             = useState(false);
  const [isAdmin, setIsAdmin]         = useState(false);
  const [isAdminLeadership, setIsAdminLeadership] = useState(false);
  const [isAdminTeam, setIsAdminTeam] = useState(false);
  const [permissions, setPermissions] = useState<Set<PermissionKey>>(new Set());

  useEffect(() => {
    // Wait until auth is resolved and we have a principal
    if (!auth.ready || !auth.email) return;

    fetch('/api/my-permissions', { credentials: 'include', cache: 'no-store' })
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        setIsAdmin(!!data.isAdmin);
        setIsAdminTeam(!!data.isAdminTeam);
        setIsAdminLeadership(!!data.isAdminLeadership);
        setPermissions(new Set(data.permissions as PermissionKey[]));
        setReady(true);
      })
      .catch(err => {
        console.warn('[PermissionsContext] Failed to load permissions:', err);
        // Fail open with empty permissions so the app doesn't hard-break
        setPermissions(new Set());
        setReady(true);
      });
  }, [auth.ready, auth.email]);

  const value = useMemo<PermissionsState>(() => ({
    ready,
    isAdmin,
    isAdminTeam,
    isAdminLeadership,
    permissions,
    hasPermission: (key: PermissionKey) => isAdmin || permissions.has(key),
  }), [ready, isAdmin, isAdminTeam, isAdminLeadership, permissions]);

  return <PermissionsCtx.Provider value={value}>{children}</PermissionsCtx.Provider>;
}
